/* =============================================================================
   REDEM MILANO — theme.js
   JavaScript principale del tema. Vanilla ES6+, niente jQuery.
   ============================================================================= */

'use strict';

/* -----------------------------------------------------------------------------
   UTILITÀ GLOBALI
   Funzioni condivise tra tutti i moduli del tema.
   ----------------------------------------------------------------------------- */

/**
 * Intrappola il focus all'interno di un elemento (drawer / modale).
 * Salva il gestore sull'elemento per poterlo rimuovere in seguito.
 * @param {HTMLElement} el - Elemento contenitore
 */
function trapFocus(el) {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const focusabili = Array.from(el.querySelectorAll(selectors)).filter(
    (node) => !node.closest('[hidden]') && !node.closest('[aria-hidden="true"]')
  );

  const primo  = focusabili[0];
  const ultimo = focusabili[focusabili.length - 1];

  function gestoreTab(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      // Shift+Tab: dal primo torna all'ultimo
      if (document.activeElement === primo) {
        e.preventDefault();
        ultimo && ultimo.focus();
      }
    } else {
      // Tab: dall'ultimo torna al primo
      if (document.activeElement === ultimo) {
        e.preventDefault();
        primo && primo.focus();
      }
    }
  }

  el.addEventListener('keydown', gestoreTab);
  el._trapFocusHandler = gestoreTab;

  // Porta immediatamente il focus sul primo elemento focusabile
  if (primo) primo.focus();
}

/**
 * Rimuove il trap focus precedentemente impostato su un elemento.
 * @param {HTMLElement} el - Elemento da cui rimuovere il trap
 */
function releaseFocus(el) {
  if (el && el._trapFocusHandler) {
    el.removeEventListener('keydown', el._trapFocusHandler);
    delete el._trapFocusHandler;
  }
}

/**
 * Ritarda l'esecuzione di una funzione fino a quando non smette di essere chiamata.
 * @param {Function} fn   - Funzione da eseguire
 * @param {number}   wait - Millisecondi di attesa (default 200)
 * @returns {Function}
 */
function debounce(fn, wait = 200) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Formatta i centesimi come valuta usando window.moneyFormat.
 * Esempio: 9900 → "€ 99,00"
 * @param {number} centesimi - Importo in centesimi
 * @returns {string}
 */
function formatMoney(centesimi) {
  const formato = window.moneyFormat || '€{{amount}}';
  const importo = (centesimi / 100).toFixed(2).replace('.', ',');
  return formato.replace('{{amount}}', importo).replace('{{amount_no_decimals}}', Math.floor(centesimi / 100));
}

/* -----------------------------------------------------------------------------
   MODULO: HEADER CON SCROLL
   Comportamento appiccicoso dell'intestazione: nasconde verso il basso,
   ricompare scorrendo verso l'alto.
   ----------------------------------------------------------------------------- */
/* -----------------------------------------------------------------------------
   MODULO: ALTEZZA BARRA ANNUNCI
   Misura l'altezza reale dell'announcement bar e aggiorna la variabile CSS
   --bar-height, in modo che l'header sticky si posizioni correttamente.
   ----------------------------------------------------------------------------- */
(function sincronizzaAltezzaBarra() {
  const bar = document.getElementById('announcement-bar');
  if (!bar) return;

  function aggiorna() {
    const h = bar.offsetHeight;
    document.documentElement.style.setProperty('--bar-height', h + 'px');
  }

  aggiorna();
  window.addEventListener('resize', debounce(aggiorna, 100), { passive: true });
})();

(function inizializzaHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;

  let scrollPrecedente = 0;
  let inAttesa         = false;

  function aggiornaHeader() {
    const scrollCorrente = window.scrollY;

    // Aggiunge .is-scrolled dopo 10 px di scorrimento
    header.classList.toggle('is-scrolled', scrollCorrente > 10);

    // Nasconde l'header dopo 100 px di scorrimento verso il basso
    if (scrollCorrente > 100) {
      if (scrollCorrente > scrollPrecedente) {
        // Scorrimento verso il basso → nascondi
        header.classList.add('is-hidden');
      } else {
        // Scorrimento verso l'alto → mostra
        header.classList.remove('is-hidden');
      }
    } else {
      // Vicino alla cima → sempre visibile
      header.classList.remove('is-hidden');
    }

    scrollPrecedente = scrollCorrente;
    inAttesa         = false;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!inAttesa) {
        window.requestAnimationFrame(aggiornaHeader);
        inAttesa = true;
      }
    },
    { passive: true }
  );
})();

/* -----------------------------------------------------------------------------
   MODULO: NAVIGAZIONE MOBILE
   Apre/chiude il pannello di navigazione laterale con trap focus e blocco
   dello scroll del body.
   ----------------------------------------------------------------------------- */
(function inizializzaNavMobile() {
  const btnApri    = document.getElementById('nav-toggle');
  const btnChiudi  = document.getElementById('nav-close');
  const nav        = document.getElementById('mobile-nav');
  const overlay    = document.getElementById('nav-overlay');

  if (!btnApri || !nav) return;

  /** Apre il pannello di navigazione mobile */
  function apriNav() {
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    overlay && overlay.classList.add('is-visible');
    btnApri.setAttribute('aria-expanded', 'true');
    // Blocca lo scroll del body
    document.body.classList.add('scroll-locked');
    document.body.style.overflow = 'hidden';
    trapFocus(nav);
  }

  /** Chiude il pannello di navigazione mobile */
  function chiudiNav() {
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    overlay && overlay.classList.remove('is-visible');
    btnApri.setAttribute('aria-expanded', 'false');
    // Sblocca lo scroll del body
    document.body.classList.remove('scroll-locked');
    document.body.style.overflow = '';
    releaseFocus(nav);
    // Riporta il focus sul bottone di apertura
    btnApri.focus();
  }

  btnApri.addEventListener('click', apriNav);
  btnChiudi && btnChiudi.addEventListener('click', chiudiNav);
  overlay   && overlay.addEventListener('click', chiudiNav);

  // Chiude con il tasto Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('is-open')) chiudiNav();
  });
})();

/* -----------------------------------------------------------------------------
   MODULO: CART DRAWER
   Apre/chiude il cassetto del carrello laterale.
   ----------------------------------------------------------------------------- */
(function inizializzaCartDrawer() {
  const drawer      = document.getElementById('cart-drawer');
  const overlay     = document.getElementById('cart-overlay');
  const btnChiudi   = document.getElementById('cart-drawer-close');
  // Raccoglie tutti i bottoni che attivano il carrello
  const bottoniApri = document.querySelectorAll('#cart-toggle, #cart-toggle-mobile');

  if (!drawer) return;

  /** Apre il cassetto del carrello */
  function apriCarrello() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay && overlay.classList.add('is-visible');
    document.body.classList.add('scroll-locked');
    document.body.style.overflow = 'hidden';
    bottoniApri.forEach((b) => b.setAttribute('aria-expanded', 'true'));
    trapFocus(drawer);
  }

  /** Chiude il cassetto del carrello */
  function chiudiCarrello() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay && overlay.classList.remove('is-visible');
    document.body.classList.remove('scroll-locked');
    document.body.style.overflow = '';
    bottoniApri.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    releaseFocus(drawer);
  }

  bottoniApri.forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    apriCarrello();
  }));
  btnChiudi && btnChiudi.addEventListener('click', chiudiCarrello);
  overlay   && overlay.addEventListener('click', chiudiCarrello);

  // Chiude con Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) chiudiCarrello();
  });

  // Espone i metodi globalmente per cart.js
  window.RedemDrawer = { apri: apriCarrello, chiudi: chiudiCarrello };
})();

/* -----------------------------------------------------------------------------
   MODULO: BADGE CONTATORE CARRELLO
   Aggiorna il numero di articoli nel badge dell'header.
   ----------------------------------------------------------------------------- */
(function inizializzaBadgeCarrello() {
  /**
   * Aggiorna tutti i badge .site-header__cart-count nella pagina.
   * Aggiunge/rimuove la classe is-visible in base al conteggio.
   * @param {number} quantita - Numero di articoli nel carrello
   */
  window.aggiornaContatorCarrello = function (quantita) {
    document.querySelectorAll('.site-header__cart-count').forEach((badge) => {
      badge.textContent = quantita > 99 ? '99+' : String(quantita);
      badge.classList.toggle('is-visible', quantita > 0);
    });
  };
})();

/* -----------------------------------------------------------------------------
   MODULO: SELETTORE LINGUA / LOCALE
   Dropdown per cambiare la lingua del negozio.
   ----------------------------------------------------------------------------- */
(function inizializzaSelettoreLingua() {
  const bottone  = document.getElementById('locale-selector');
  const dropdown = document.getElementById('locale-dropdown-desktop');

  if (!bottone || !dropdown) return;

  /** Apre il dropdown della lingua */
  function apri() {
    dropdown.hidden = false;
    bottone.setAttribute('aria-expanded', 'true');
  }

  /** Chiude il dropdown della lingua */
  function chiudi() {
    dropdown.hidden = true;
    bottone.setAttribute('aria-expanded', 'false');
  }

  bottone.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.hidden ? apri() : chiudi();
  });

  // Chiude quando si clicca fuori dal componente
  document.addEventListener('click', (e) => {
    if (!bottone.contains(e.target) && !dropdown.contains(e.target)) {
      chiudi();
    }
  });

  // Chiude con Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !dropdown.hidden) {
      chiudi();
      bottone.focus();
    }
  });
})();

/* -----------------------------------------------------------------------------
   MODULO: ACCORDION
   Gestisce l'apertura e chiusura dei pannelli accordion accessibili.
   ----------------------------------------------------------------------------- */
(function inizializzaAccordion() {
  /**
   * Collega i trigger accordion a tutti i contenitori specificati.
   * Può essere chiamato anche da product.js per accordions specifici.
   * @param {HTMLElement|Document} contenitore - Elemento radice in cui cercare
   */
  window.inizializzaAccordionIn = function (contenitore) {
    const triggers = (contenitore || document).querySelectorAll('.accordion__trigger');

    triggers.forEach((trigger) => {
      // Evita di collegare più volte lo stesso listener
      if (trigger._accordionInit) return;
      trigger._accordionInit = true;

      trigger.addEventListener('click', () => {
        const espanso  = trigger.getAttribute('aria-expanded') === 'true';
        const pannello = trigger.nextElementSibling;

        if (!pannello || !pannello.classList.contains('accordion__panel')) return;

        if (espanso) {
          // Chiudi il pannello
          trigger.setAttribute('aria-expanded', 'false');
          pannello.hidden = true;
        } else {
          // Apri il pannello
          trigger.setAttribute('aria-expanded', 'true');
          pannello.hidden = false;
        }
      });

      // Supporto tastiera: Spazio e Invio
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trigger.click();
        }
      });
    });
  };

  // Inizializza gli accordion presenti al caricamento della pagina
  window.inizializzaAccordionIn(document);
})();

/* -----------------------------------------------------------------------------
   MODULO: TOAST NOTIFICATIONS
   Mostra messaggi temporanei all'utente (conferme, errori, ecc.)
   ----------------------------------------------------------------------------- */
(function inizializzaToast() {
  let timerToast = null;

  /**
   * Mostra un messaggio toast nella pagina.
   * @param {string} messaggio  - Testo da mostrare
   * @param {number} durata     - Durata in millisecondi (default 3000)
   */
  window.showToast = function (messaggio, durata = 3000) {
    const toast = document.querySelector('.toast');
    if (!toast) return;

    // Aggiorna il testo in modo sicuro
    const testoEl = toast.querySelector('.toast__message') || toast;
    testoEl.textContent = messaggio;

    // Annulla eventuale timer precedente
    if (timerToast) clearTimeout(timerToast);

    // Mostra il toast
    toast.classList.add('is-visible');
    toast.setAttribute('aria-live', 'polite');

    // Nasconde automaticamente dopo la durata specificata
    timerToast = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, durata);
  };
})();

/* -----------------------------------------------------------------------------
   MODULO: BARRA ANNUNCI
   Permette di chiudere la barra degli annunci e ricorda la scelta.
   ----------------------------------------------------------------------------- */
(function inizializzaBarraAnnunci() {
  const btnChiudi = document.getElementById('announcement-close');
  if (!btnChiudi) return;

  const barra = btnChiudi.closest('.announcement-bar');
  if (!barra) return;

  btnChiudi.addEventListener('click', () => {
    // Animazione di uscita con altezza
    barra.style.maxHeight = barra.offsetHeight + 'px';
    barra.style.overflow  = 'hidden';
    barra.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';

    requestAnimationFrame(() => {
      barra.style.maxHeight = '0';
      barra.style.opacity   = '0';
    });

    barra.addEventListener('transitionend', () => {
      barra.hidden = true;
      barra.style.cssText = '';
    }, { once: true });

    // Ricorda la preferenza per la sessione corrente
    try {
      sessionStorage.setItem('redem_annuncio_chiuso', '1');
    } catch (_) {
      // sessionStorage non disponibile in alcuni contesti
    }
  });

  // Nasconde subito se già chiusa in questa sessione
  try {
    if (sessionStorage.getItem('redem_annuncio_chiuso')) {
      barra.hidden = true;
    }
  } catch (_) {}
})();
